import { useCallback, useMemo } from 'react';

import UILink from '@UI/Link';
import useNavigation from '@Navigation/useNavigation';
import transformUrl from '@Navigation/transformUrl';
import { useRequest } from '@Contexts/Request';
import { useDeps } from '@Contexts/DI';
import useIsTerminal from '@Hooks/useIsTerminal';

import type { FC, MouseEvent } from 'react';
import type { LinkProps as UILinkProps } from '@UI/Link';
import type { Region } from '@Navigation/useNavigation';

export interface LinkProps extends UILinkProps {
  className?: string;
  region?: Region;
  language?: string;
  preventDefault?: boolean;
  to?: string;
}

const Link: FC<LinkProps> = (props) => {
  const {
    to = '',
    language,
    region,
    preventDefault,
    children,
    target,
    onClick,
    ...restProps
  } = props;
  const navigation = useNavigation();
  const request = useRequest();
  const { analytics } = useDeps();
  const isTerminal = useIsTerminal();

  const href = useMemo(() => {
    return transformUrl({
      url: to,
      targetRegion: region?.url?.replace('/', ''),
      targetLanguage: language,
    });

    // Нам нужен `request` в зависимостях, чтобы пересчитать ссылки при смене языка или региона
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, language, to, request]);

  const { isExternal, isPhone } = useMemo(() => {
    return {
      isExternal: !!to?.match(/^(http:\/\/|https:\/\/|mailto:)/),
      isPhone: to?.startsWith('tel:'),
    };
  }, [to]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (window.cancelClick) {
        e.preventDefault();
        return;
      }

      if (isExternal) {
        analytics.dispatchEvent('link.external.click', { link: to });
      }

      if (isPhone) {
        analytics.dispatchEvent('link.phone.click');
      }

      if (onClick) onClick(e);

      if (preventDefault) {
        e.preventDefault();
        return;
      }

      // При соблюдении этих условий ссылки должны работать как нативные
      if (!href) return;
      if (target === '_blank') return;
      if (isExternal) return;
      if (isPhone) return;
      if (isTerminal) return;

      e.preventDefault();

      if (href.includes('#')) {
        const changeHash = new CustomEvent('changeHash');
        navigation.replaceUrl(href);
        window.dispatchEvent(changeHash);
        return;
      }

      navigation.openPage({ url: href, region, language });
    },
    [
      isExternal,
      isPhone,
      onClick,
      preventDefault,
      href,
      target,
      isTerminal,
      navigation,
      region,
      language,
      analytics,
      to,
    ],
  );

  return (
    <UILink
      {...restProps}
      to={href}
      target={isExternal ? '_blank' : target}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      onClick={handleClick}
    >
      {children}
    </UILink>
  );
};

export default Link;
